import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, withKeys as _withKeys, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, resolveDirective as _resolveDirective, createTextVNode as _createTextVNode, unref as _unref, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { id: "main-content" }
const _hoisted_2 = {
  class: "modal",
  id: "modal-loading"
}
const _hoisted_3 = ["inert"]
const _hoisted_4 = { id: "left-buttons" }
const _hoisted_5 = { class: "location-display" }
const _hoisted_6 = {
  id: "location-title",
  class: "event-title"
}
const _hoisted_7 = {
  key: 0,
  class: "mb-1"
}
const _hoisted_8 = { id: "geolocation-close" }
const _hoisted_9 = { id: "geolocation-controls" }
const _hoisted_10 = { class: "options" }
const _hoisted_11 = { id: "right-buttons" }
const _hoisted_12 = {
  id: "date-title",
  class: "event-title"
}
const _hoisted_13 = { class: "mb-1" }
const _hoisted_14 = { class: "date-buttons" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "date-picker-section date-button" }
const _hoisted_17 = ["color"]
const _hoisted_18 = ["inert"]
const _hoisted_19 = { id: "time-slider-chips" }
const _hoisted_20 = { class: "slider-container" }
const _hoisted_21 = { class: "thumb-label" }
const _hoisted_22 = { class: "time-chips" }
const _hoisted_23 = {
  key: 1,
  id: "small-play-pie-chart"
}
const _hoisted_24 = ["inert"]
const _hoisted_25 = ["inert"]
const _hoisted_26 = { class: "video-wrapper" }
const _hoisted_27 = { class: "ml-5 pt-3" }
const _hoisted_28 = { class: "pb-3" }

import { ref, reactive, computed, markRaw, onMounted, nextTick, watch } from "vue";
import { useDisplay } from "vuetify";
import { storeToRefs } from "pinia";
import { getTimezoneOffset } from "date-fns-tz";
import tzlookup from "tz-lookup";
import { v4 } from "uuid";

import { AstroTime, Seasons } from "astronomy-engine";

import { Color, Grids, Planets, Settings, WWTControl } from "@wwtelescope/engine";
import { GotoRADecZoomParams, engineStore } from "@wwtelescope/engine-pinia";
import {
  BackgroundImageset,
  LocationDeg,
  skyBackgroundImagesets,
  supportsTouchscreen,
  blurActiveElement,
  useWWTKeyboardControls,
  D2R,
  R2D,
  API_BASE_URL,
} from "@cosmicds/vue-toolkit";
import { MapBoxFeature, MapBoxFeatureCollection, geocodingInfoForSearch, textForLocation } from "@cosmicds/vue-toolkit/src/mapbox";

import { useTimezone } from "./timezones";
import { dayFractionForTimestamp, equatorialToHorizontal, horizontalToEquatorial } from "./utils";
import { resetNSEWText, drawPlanets, renderOneFrame, drawEcliptic, drawSkyOverlays } from "./wwt-hacks";
import { useSun } from "./composables/useSun";
import { formatInTimeZone } from "date-fns-tz";
import { sunPlace } from "./horizon_sky";

type SheetType = "text" | "video";
type CameraParams = Omit<GotoRADecZoomParams, "instant">;
export interface SeasonsStoryProps {
  wwtNamespace?: string;
  initialCameraParams?: CameraParams;
}

const sliderMin = 0;
const sliderMax = 500;
const highAltDeg = 70;
type EventOfInterest = typeof EVENTS_OF_INTEREST[number];

const NIGHTTIME = "rgb(120, 120, 120)";

interface LocationInfo {
  name: string;
  latitude: string;
  longitude: string;
}

const MAX_ZOOM = 500;

const infoFraction = 34;
const OPT_OUT_KEY = "seasons-optout" as const;
const AHA_OPT_OUT_KEY = "seasons-aha-optout" as const;
const UUID_KEY = "seasons-uuid" as const;

export default /*@__PURE__*/_defineComponent({
  __name: 'SeasonsStory',
  setup(__props) {

const store = engineStore();
const {
  currentTime,
} = storeToRefs(store);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const wwtSettings: Settings = Settings.get_active();

useWWTKeyboardControls(store);

const touchscreen = supportsTouchscreen();
const { smAndDown } = useDisplay();

const splash = new URLSearchParams(window.location.search).get("splash")?.toLowerCase() !== "false";
const showSplashScreen = ref(splash);
const splashReady = computed(() => splash && selectedEvent.value !== null);
const splashScreenRef = ref<HTMLElement | null>(null);
const backgroundImagesets = reactive<BackgroundImageset[]>([]);
const sheet = ref<SheetType | null>(null);
const layersLoaded = ref(false);
const positionSet = ref(false);
const trackSun = ref(true);
const pathInFoV = ref(false);
const forceCamera = computed(() => trackSun.value && !pathInFoV.value);
const showQuestion = ref(false);
let questionTimeout: ReturnType<typeof setTimeout> | null = null;


const tab = ref(0);
let howToUseTimeMs = 0;
let whatToExploreTimeMs = 0;
let howToUseStartTimestamp = null as number | null;
let whatToExploreStartTimestamp = null as number | null;

const playing = ref(false);
const showLocationSelector = ref(false);
const showWebGL2Dialog = ref(false);

const showHorizon = ref(true);

const startAzOffset = ref(40 * D2R);
const endAzOffset = ref(-startAzOffset.value);
const azOffsetSlope = computed(() => (endAzOffset.value - startAzOffset.value) / (endTime.value - startTime.value));

const sunDistance = ref(sunPlace.get_distance());

// Get the next 4 "dates of interest"
// i.e. equinoxes and solstices
const currentDate = new Date();
const currentYear = currentDate.getUTCFullYear();
const datesOfInterest = Seasons(currentYear);

// Find dates that have passed and sort them by date
const pastEvents = Object.entries(datesOfInterest)
  .filter(([_key, value]: [string, AstroTime]) => value.date < currentDate)
  .map(entry => entry[0]);

// If we have more than 1 past event, replace the older ones with next year's events
// Keep only the most recent past event as our starting point
if (pastEvents.length > 0) {
  const nextSeasonsInfo = Seasons(currentYear + 1);
  pastEvents.forEach(key => {
    datesOfInterest[key] = nextSeasonsInfo[key];
  });
}

const startTime = ref(0);
const endTime = ref(0);
const sliderRange = sliderMax - sliderMin;
const sliderValue = computed({
  get() {
    const fraction = (currentTime.value.getTime() - startTime.value) / (endTime.value - startTime.value);
    return sliderMin + fraction * sliderRange;
  },
  set(value: number) {
    const fraction = (value - sliderMin) / sliderRange;
    const time = fraction * (endTime.value - startTime.value) + startTime.value;
    store.setTime(new Date(time));
  }
});

const middayTime = computed(() => 0.5 * (startTime.value + endTime.value));
const middayAltAz = computed(() => getSunPositionAtTime(new Date(middayTime.value)));
const highAltTimes = computed(() => {
  const { rising: start, setting: end} = getTimeforSunAlt(highAltDeg, middayTime.value);
  return { start, end }; 
});
const highAltCoordinates = computed(() => {
  if (!(highAltTimes.value.start && highAltTimes.value.end)) {
    return { start: null, end : null };
  }
  return {
    start: getSunPositionAtTime(new Date(highAltTimes.value.start)),
    end: getSunPositionAtTime(new Date(highAltTimes.value.end)),
  };
});

const sortedDatesOfInterest = computed(() => {
  const entries: ([EventOfInterest, AstroTime])[] = Object.entries(datesOfInterest) as [EventOfInterest, AstroTime][];
  const sortedEntries = entries.sort((a, b) => a[1].date.getTime() - b[1].date.getTime());
  
  // Add "today" as the first entry
  const today: [EventOfInterest, AstroTime] = ["today", { date: new Date() } as AstroTime];
  return [today, ...sortedEntries];
});

const EVENTS_OF_INTEREST = [
  "today",
  "mar_equinox",
  "jun_solstice",
  "sep_equinox",
  "dec_solstice",
  "custom",
] as const;
const selectedEvent = ref<EventOfInterest | null>(null);
// const selectedDateType = ref<DateSelection | null>(null);
const selectedCustomDate = ref<Date | null>(null);
const showDatePicker = ref(false);

const getDateForEvent = (event: EventOfInterest): Date => {
  if (event === "today") {
    return new Date();
  } else if (event === "custom" && selectedCustomDate.value) {
    return selectedCustomDate.value;
  } else {
    return datesOfInterest[event].date;
  }
};


const handleDateSelection = (date: Date | null) => {
  if (date) {
    showDatePicker.value = false;
    selectedCustomDate.value = date;
    selectedEvent.value = 'custom';
  }
};

function eventName(event: EventOfInterest): string {
  const isSmall = smallSize.value;
  switch (event) {
  case "today":
    return "Today";
  case "mar_equinox":
    return isSmall ? "Mar Equinox" : "March Equinox";
  case "jun_solstice":
    return isSmall ? "Jun Solstice" : "June Solstice";
  case "sep_equinox":
    return isSmall ? "Sep Equinox" : "September Equinox";
  case "dec_solstice":
    return isSmall ? "Dec Solstice" : "December Solstice";
  case "custom":
    return "Custom";
  }
}

function getCurrentSeason(event: string, latitude: number): 'spring' | 'summer' | 'autumn' | 'winter' {

  if (latitude >= 0) {
    switch (event) {
    case "mar_equinox":
      return "spring";
    case "jun_solstice":
      return "summer";
    case "sep_equinox":
      return "autumn";
    case "dec_solstice":
      return "winter";      
    }
  } else {
    switch (event) {
    case "mar_equinox":
      return "autumn";
    case "jun_solstice":
      return "winter";
    case "sep_equinox":
      return "spring";
    case "dec_solstice":
      return "summer";      
    }
  }
  return "spring"; // fallback
}

function getCurrentSeasonForDate(date: Date, latitude: number): 'spring' | 'summer' | 'autumn' | 'winter' {
  const year = date.getFullYear();
  const seasonsForYear = Seasons(year);
  
  // Get the season dates for the year
  const marEquinox = seasonsForYear.mar_equinox.date;
  const junSolstice = seasonsForYear.jun_solstice.date;
  const sepEquinox = seasonsForYear.sep_equinox.date;
  const decSolstice = seasonsForYear.dec_solstice.date;
  
  // Determine which season the date falls into
  let season: 'spring' | 'summer' | 'autumn' | 'winter';
  
  const getDate = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  
  if (getDate(date) >= getDate(marEquinox) && getDate(date) < getDate(junSolstice)) {
    season = 'spring';
  } else if (getDate(date) >= getDate(junSolstice) && getDate(date) < getDate(sepEquinox)) {
    season = 'summer';
  } else if (getDate(date) >= getDate(sepEquinox) && getDate(date) < getDate(decSolstice)) {
    season = 'autumn';
  } else {
    // Either before March equinox or after December solstice (winter)
    season = 'winter';
  }
  
  // Adjust for hemisphere
  if (latitude < 0) {
    // Southern hemisphere - seasons are opposite
    switch (season) {
    case 'spring': return 'autumn';
    case 'summer': return 'winter';
    case 'autumn': return 'spring';
    case 'winter': return 'summer';
    }
  }
  
  return season;
}

const seasonalColors = {
  spring: '#FFA0D7',  
  summer: '#F7EB67',  
  autumn: '#FEB770',  
  winter: '#c1e2fc'   
};

const accentColor = computed(() => {

  const event = selectedEvent.value;
  if (!event) {
    return seasonalColors.spring;
  }
  const latitude = selectedLocation.value.latitudeDeg;
  // Handle "today" case
  if (event === 'today') {
    const today = new Date();
    const currentSeason = getCurrentSeasonForDate(today, latitude);
    return seasonalColors[currentSeason];
  }
  
  // Handle custom date case
  if (event === 'custom' && selectedCustomDate.value) {
    const customSeason = getCurrentSeasonForDate(selectedCustomDate.value, latitude);
    return seasonalColors[customSeason];
  }
  
  const currentSeason = getCurrentSeason(event, latitude);
  return seasonalColors[currentSeason];
});

const displayedDate = computed(() => {
  if (selectedEvent.value === 'today') {
    return new Date();
  } else if (selectedEvent.value === 'custom' && selectedCustomDate.value) {
    return selectedCustomDate.value;
  } else if (selectedEvent.value) {
    return datesOfInterest[selectedEvent.value].date;
  }
  return new Date(); // fallback
});

const currentDayInfo = computed(() => {
  const day = getDateForEvent(selectedEvent.value || 'today');
  return getStartAndEndTimes(day);
});

const sunAlways = computed<"up" | "down" | null>(() => {
  let polarInfo: ReturnType<typeof getStartAndEndTimes>[2] | null = null;
  if (selectedEvent.value != null) {
    polarInfo = currentDayInfo.value[2];
  } else if (selectedCustomDate.value != null) {
    polarInfo = getStartAndEndTimes(selectedCustomDate.value)[2];
  }
  return polarInfo != null ? 
    (polarInfo.sunAlwaysUp ? "up" : (polarInfo.sunAlwaysDown ? "down" : null)) :
    null;
});

function isWebGL2Enabled(): boolean {
  // It doesn't seem like there's a better way to do this than just to try and get a context
  // https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Detect_WebGL
  // NB: The engine specifically wants a webgl2 context
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl2");
  return gl instanceof WebGL2RenderingContext;
}

function updatePathInFoV() {
  // If the Sun is always up, no need to do the width check, which is problematic then anyways
  if (sunAlways.value === "up") {
    pathInFoV.value = false;
    return;
  }
  const startPos = getSunPositionAtTime(new Date(startTime.value));
  const endPos = getSunPositionAtTime(new Date(endTime.value));
  const context = WWTControl.singleton.renderContext;
  let azWidth = Math.abs(endPos.azRad - startPos.azRad) * R2D;
  const widthToFit = 47.8 * Math.log(0.9 * context.width) - 245;
  const peakNorth = Math.min(Math.abs(middayAltAz.value.azRad), Math.abs(middayAltAz.value.azRad - 2 * Math.PI)) < Math.abs(middayAltAz.value.azRad - Math.PI);
  if (peakNorth) {
    azWidth = 360 - azWidth;
  }
  pathInFoV.value = (azWidth < widthToFit);
}

function dayString(date: Date) {
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: smallSize.value ? "short" : "long",
    day: "numeric",
  });
}

function formatDayLength(milliseconds: number, polarInfo: { sunAlwaysUp: boolean; sunAlwaysDown: boolean }): string {
  if (polarInfo.sunAlwaysDown) {
    return "0h 0m";
  }
  
  if (polarInfo.sunAlwaysUp) {
    return "24h 0m";
  }
  
  // Normal calculation for days with sunrise/sunset
  const totalMinutes = Math.round(milliseconds / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

function goToSun() {
  const sunPos = getSunPositionAtTime(currentTime.value);
  const radec = horizontalToEquatorial(
    sunPos.altRad, 
    sunPos.azRad, 
    selectedLocation.value.latitudeDeg * D2R,
    selectedLocation.value.longitudeDeg * D2R,  
    currentTime.value
  );
  store.gotoRADecZoom({
    ...radec,
    zoomDeg: Math.min(store.zoomDeg, 180),
    instant: false,
  });
}
function getStartAndEndTimes(day: Date): [Date, Date, { sunAlwaysUp: boolean; sunAlwaysDown: boolean }] {
  const time = day.getTime();
  const { rising: dayStart, setting: dayEnd, always } = getTimeforSunAlt(0, time);

  let start: Date;
  let end: Date;
  const sunAlwaysUp = always === 'up';
  const sunAlwaysDown = always === 'down';

  if (dayStart === null || dayEnd === null) {
    console.log("Polar day or night detected");
    // moved checking for polar day/night to useSun
    
    // It don't know if it really make sense to have the slider go 
    // from 00:00 to 23:59 when for both polar night and noon
    //  But I think that is the only choice we have
    // utcMidnight = time - (time % (24 * 60 * 60 * 1000))
    // localMidnight = utcMidnight - timezone offset
    const localMidnight = time - (time % (24 * 60 * 60 * 1000)) - selectedTimezoneOffset.value;
    start = new Date(localMidnight);
    end = new Date(localMidnight + 86400000 - 60);
  } else {
    start = new Date(dayStart);
    end = new Date(dayEnd);
  }

  return [start, end, { sunAlwaysUp, sunAlwaysDown }];
}

function updateSliderBounds(_newLocation: LocationDeg, oldLocation: LocationDeg) {
  if (selectedEvent.value === null) {
    return;
  }
  const [start, end, _polarInfo] = getStartAndEndTimes(getDateForEvent(selectedEvent.value));
  startTime.value = start.getTime();
  endTime.value = end.getTime();

  const oldOffset = getTimezoneOffset(tzlookup(oldLocation.latitudeDeg, oldLocation.longitudeDeg));

  const diff = oldOffset - selectedTimezoneOffset.value;
  let newSelectedTime = currentTime.value.getTime() + diff;
  newSelectedTime = Math.min(Math.max(startTime.value, newSelectedTime), endTime.value);
  selectedTime.value = newSelectedTime;
  
  // Update time in WWT to reflect the new location's timing
  store.setTime(new Date(newSelectedTime));
}

function handlePlaying(play: boolean) {
  if (forceCamera.value) {
    resetView(MAX_ZOOM);
  }
  // Auto-pause when time reaches sunset or sunrise, accounting for playing direction
  if (playing.value && ((currentTime.value.getTime() >= endTime.value && store.clockRate >= 0) || ( currentTime.value.getTime() <= startTime.value && store.clockRate <= 0))) {
    playing.value = false;
    return;
  }  

  playing.value = play;
  wwtStats.playPauseCount += 1;
  events.push(play ? 'wwt_play' : 'wwt_pause');
}

function centerOnMidday(altitudeDeg: number | null =null) {
  let altToUse: number;
  if (altitudeDeg == null) {
    const currentAltAz = equatorialToHorizontal(
      store.raRad,
      store.decRad,
      selectedLocation.value.latitudeDeg * D2R,
      selectedLocation.value.longitudeDeg * D2R,
      store.currentTime,
    );
    altToUse = currentAltAz.altRad;
  } else {
    altToUse = altitudeDeg * D2R;
  }
  const middayRADec = horizontalToEquatorial(
    altToUse,
    middayAltAz.value.azRad,
    selectedLocation.value.latitudeDeg * D2R,
    selectedLocation.value.longitudeDeg * D2R,
    store.currentTime,
  );
  store.gotoRADecZoom({
    raRad: middayRADec.raRad,
    decRad: middayRADec.decRad,
    zoomDeg: store.zoomDeg,
    instant: true,
  });
}

function goToEvent(event: EventOfInterest) {
  const day = getDateForEvent(event);
  if (event !== 'custom') {
    selectedCustomDate.value = day;
  }
  updateSliderBounds(selectedLocation.value, selectedLocation.value);
  resetView();
}

const wwtStats = markRaw({
  timeResetCount: 0,
  reverseCount: 0,
  playPauseCount: 0,
  speedups: [] as number[],
  slowdowns: [] as number[],
  rateSelections: [] as number[],
  startTime: Date.now(),
});

// get lat and lon url query parameters for initial location
const urlParams = new URLSearchParams(window.location.search);
const urlLat = urlParams.get("lat");
const urlLon = urlParams.get("lon");
let initialLat: number = 42.3581;
let initialLon: number = -71.1056;
if (urlLat && urlLon) {
  initialLat = parseFloat(urlLat);
  initialLon = parseFloat(urlLon);
}
const selectedLocation = ref<LocationDeg>({
  longitudeDeg: initialLon,
  latitudeDeg: initialLat,
  // latitudeDeg: 72.40 // test polar latitude
});

function createQueryUrl(): string {
  const lat = `${selectedLocation.value.latitudeDeg}`;
  const lon = `${selectedLocation.value.longitudeDeg}`;
  const params = new URLSearchParams(window.location.search);
  params.set("lat", lat);
  params.set("lon", lon);
  return `${window.location.origin}/?${params.toString()}`;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).createQueryUrl = createQueryUrl;

const selectedLocationInfo = ref<LocationInfo>({ name: "", latitude: "", longitude: "" });
const searchErrorMessage = ref<string | null>(null);
const geocodingOptions = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  access_token: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN ?? "", 
};

function updateLocationFromMap(location: LocationDeg) {
  console.log("Updating location from map:", location);
  selectedLocation.value = location;
  userSelectedLocations.push([location.latitudeDeg, location.longitudeDeg]);
  events.push(`location_update ${location.latitudeDeg} ${location.longitudeDeg}`);
}

function latText(latitudeDeg: number): string {
  const ns = latitudeDeg >= 0 ? 'N' : 'S';
  const lat = Math.abs(latitudeDeg).toFixed(2);
  return `${lat}° ${ns}`;
}

function lonText(longitudeDeg: number): string {
  const ew = longitudeDeg >= 0 ? 'E' : 'W';
  const lon = Math.abs(longitudeDeg).toFixed(2);
  return `${lon}° ${ew}`;
}

async function getLocationInfo(longitudeDeg: number, latitudeDeg: number): Promise<LocationInfo> {
  let location: string = "";
  // eslint-disable-next-line @typescript-eslint/naming-convention
  try {
    location = await textForLocation(longitudeDeg, latitudeDeg, geocodingOptions);
  } catch (err) {
    console.error("Error getting location text:", err);
  }
  const locationName = !startsWithNumber(location) ? `${location}` : "";
  const formattedLat = latText(latitudeDeg);
  const formattedLon = lonText(longitudeDeg);

  return {
    name: locationName,
    latitude: formattedLat,
    longitude: formattedLon,
  };
}

function startsWithNumber(text: string): boolean {
  return text.charAt(0).match(/[0-9]|-/) !== null;
}

function setLocationFromFeature(feature: MapBoxFeature) {
  selectedLocation.value = { longitudeDeg: feature.center[0], latitudeDeg: feature.center[1] };
  getLocationInfo(feature.center[0], feature.center[1]).then(locationInfo => {
    selectedLocationInfo.value = locationInfo;
  }).catch(_err => {
    searchErrorMessage.value = "An error occurred while searching";
  });
  userSelectedLocations.push(feature.center);
  events.push(`location_update ${feature.center[0]} ${feature.center[1]}`);
}

function setLocationFromSearchFeature(feature: MapBoxFeature) {
  setLocationFromFeature(feature);
}

async function updateSelectedLocationInfo() {
  const locationInfo = await getLocationInfo(selectedLocation.value.longitudeDeg, selectedLocation.value.latitudeDeg);
  selectedLocationInfo.value = locationInfo;
}

function searchProvider(text: string): Promise<MapBoxFeatureCollection> {
  return geocodingInfoForSearch(text, geocodingOptions);
}

const selectedTime = ref(Date.now());
// setInterval(() => {
//   if (playing.value) {
//     selectedTime.value = currentTime.value.getTime();
//   }
// }, 40);

const { selectedTimezone, selectedTimezoneOffset } = useTimezone(selectedLocation);
  
const { getTimeforSunAlt, getSunPositionAtTime } = useSun({
  store,
  location: selectedLocation,
  selectedTime: currentTime,
  selectedTimezoneOffset,
  zoomLevel: 360,
});

const selectedLocaledTimeDateString = computed(() => {
  const formatString = "h:mm aa (zzz)";
  return formatInTimeZone(currentTime.value, selectedTimezone.value, formatString);
});

// import { getTimezoneOffset } from "date-fns-tz";
// const testTime = ref(new Date('Sep 22 2025 00:00:00 GMT-0400').getTime());
// const testSun = useSun({
//   store,
//   location: selectedLocation,
//   selectedTime: testTime,
//   selectedTimezoneOffset: getTimezoneOffset("America/New_York", testTime.value),
//   zoomLevel: 360,
// });
// console.log("Testing sun values");
// const { rising: testRising, setting: testSetting } = testSun.getTimeforSunAlt(0);
// if (testRising !== null && testSetting !== null) {
//   console.log(`Test Sun Rising: ${new Date(testRising)}`);
//   console.log(`Test Sun Setting: ${new Date(testSetting)}`);
// }

function aspectRatioSetup() {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error WWTControl does have a canvas element (that's not TS-exposed)
  const canvas = WWTControl.singleton.canvas as HTMLCanvasElement;

  function updateAzOffsets() {
    const aspectRatio = canvas.width / canvas.height;
    startAzOffset.value = 0.35 * (60 * aspectRatio + 13.5) * D2R;
    endAzOffset.value = -startAzOffset.value;
  }

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === canvas) {
        updateAzOffsets();
        resetView();
        return;
      }
    }
  });

  observer.observe(canvas);
  updateAzOffsets();
}

onMounted(() => {

  if (!isWebGL2Enabled()) {
    closeSplashScreen();
    showWebGL2Dialog.value = true; 
    layersLoaded.value = true;
    positionSet.value = true;
    // eslint-disable-next-lint @typescript-eslint/ban-ts-comment
    // @ts-expect-error `canvas` is defined
    WWTControl.singleton.canvas.setAttribute("hidden", "true");
    WWTControl.singleton.renderOneFrame = function() {};
    return;
  }

  updateSelectedLocationInfo();
  store.waitForReady().then(async () => {
    WWTControl.singleton.set_zoomMax(MAX_ZOOM);
    skyBackgroundImagesets.forEach(iset => backgroundImagesets.push(iset));

    updateWWTLocation(selectedLocation.value);
    
    aspectRatioSetup();

    store.setClockSync(false);
    store.setClockRate(0);

    // Adding Alt-Az grid here
    store.applySetting(["showAltAzGrid", true]);
    store.applySetting(["showAltAzGridText", true]);
    store.applySetting(["altAzGridColor", Color.fromArgb(255, 255, 255, 255)]);
    store.applySetting(["localHorizonMode", true]);

    doWWTModifications();

    // Set the initial event after everything is ready
    selectedEvent.value = sortedDatesOfInterest.value[0][0];

    // If there are layers to set up, do that here!
    positionSet.value = true;
    layersLoaded.value = true;

    questionDisplaySetup();
  });

  createUserEntry();
});

const ready = computed(() => layersLoaded.value && positionSet.value);

/* `isLoading` is a bit redundant here, but it could potentially have independent logic */
const isLoading = computed(() => !ready.value);

const inNorthernHemisphere = computed(() => selectedLocation.value.latitudeDeg > 0);

/* Properties related to device/screen characteristics */
const smallSize = computed(() => smAndDown.value);
const mobile = computed(() => smallSize.value && touchscreen);

const infoSheetLocation = computed(() => smallSize.value ? "bottom" : "left");
const infoSheetHeight = computed(() => infoSheetLocation.value === "bottom" ? `${infoFraction}%` : "100%");
const infoSheetWidth = computed(() => infoSheetLocation.value === "bottom" ? "100%" : `${infoFraction}%`);
const infoTextHeight = computed(() => infoSheetLocation.value === "bottom" ? `calc(${infoFraction}vh - 25px)` : "calc(100vh - 25px)");
const infoSheetTransition = computed(() => infoSheetLocation.value === "bottom" ? "dialog-bottom-transition" : "slide-x-transition");

/* This lets us inject component data into element CSS */
const cssVars = computed(() => {
  return {
    "--accent-color": accentColor.value,
    "--app-content-height": showTextSheet.value && infoSheetLocation.value === "bottom" ? `${100 - infoFraction}vh` : "100dvh",
    "--app-content-width": showTextSheet.value && infoSheetLocation.value === "left" ? `${100 - infoFraction}vw` : "100dvw",
    "--time-slider-width": `${daylightPercentage.value.toFixed(2)}%`,
    "--time-slider-left": `${(100 * Math.min(1, dayFractionForTimestamp(startTime.value + selectedTimezoneOffset.value))).toFixed(2)}%`,
    "--thumb-label-color": sunAlways.value === "down" ? NIGHTTIME : accentColor.value,
    "--info-sheet-width": infoSheetWidth.value,
    "--info-sheet-height": infoSheetHeight.value,
    "--info-text-height": infoTextHeight.value,
  };
});

const daylightPercentage = computed(() => 100 * Math.min(1, dayFractionForTimestamp(endTime.value - startTime.value)));



/**
  Computed flags that control whether the relevant dialogs display.
  The `sheet` data member stores which sheet is open, so these are just
  computed wrappers around modifying/querying that which can be used as
  dialog v-model values
*/
const showTextSheet = computed({
  get() {
    return sheet.value === "text";
  },
  set(_value: boolean) {
    selectSheet("text");
  }
});

const showVideoSheet = computed({
  get() {
    return sheet.value === "video";
  },
  set(value: boolean) {
    selectSheet("video");
    if (!value) {
      const video = document.querySelector("#info-video") as HTMLVideoElement;
      video.pause();
    }
  }
});

/**
  This is convenient if there's any other logic that we want to run
  when the splash screen is closed
*/
function closeSplashScreen() {
  showSplashScreen.value = false;
}

function selectSheet(sheetType: SheetType | null) {
  if (sheet.value === sheetType) {
    sheet.value = null;
    nextTick(() => {
      blurActiveElement();
    });
  } else {
    sheet.value = sheetType;
  }
}

function resetView(zoomDeg?: number, withAzOffset=true) {

  let altDeg = 33;
  updatePathInFoV();
  if (pathInFoV.value) {
    setTimeout(() => centerOnMidday(altDeg), 100);
    return;
  }

  const time = store.currentTime;
  const t = time.getTime();

  const latRad = selectedLocation.value.latitudeDeg * D2R;
  const lonRad = selectedLocation.value.longitudeDeg * D2R;

  const sunAltAz = getSunPositionAtTime(time);
  let az = sunAltAz.azRad;

  const middayAltDeg = middayAltAz.value.altRad * R2D;
  const middayAzDeg = middayAltAz.value.azRad * R2D;
  const peakNorth = Math.min(Math.abs(middayAzDeg), Math.abs(middayAzDeg - 360)) < Math.abs(middayAzDeg - 180);
  if (middayAltDeg > highAltDeg) {

    // Altitude modifications
    const dayFrac  = (time.getTime() - startTime.value) / (endTime.value - startTime.value);
    const dayAzFactor = -2 * Math.abs(0.5 - dayFrac) + 1;
    altDeg = middayAltDeg * dayAzFactor;
    altDeg = Math.max(90 - Math.abs(altDeg - 90), 33);

    // Azimuth modifications
    const { start: highStartTime, end: highEndTime } = highAltTimes.value;
    const { start, end } = highAltCoordinates.value;
    if (highStartTime && highEndTime && start && end && t > highStartTime && t < highEndTime) {
      const topStartAz = start.azRad;
      let topEndAz = end.azRad;
      const tSouth = (Math.PI - topStartAz) / (topEndAz - topStartAz);
      const passThroughSouth = tSouth >= 0 && tSouth <= 1;
      if (peakNorth === passThroughSouth) {
        topEndAz -= 2 * Math.PI;
      }

      const tTop = (t - highStartTime) / (highEndTime - highStartTime);
      az = tTop * (topEndAz - topStartAz) + topStartAz;
    }
  }
  const alt = altDeg * D2R;

  if (t > 0 && withAzOffset) {
    const offset = (azOffsetSlope.value * (t - startTime.value) + startAzOffset.value);
    const sgn = peakNorth ? -1 : 1;
    az += (offset * sgn);
  }
  const raDec = horizontalToEquatorial(
    alt,
    az,
    latRad,
    lonRad,
    time,
  );

  store.gotoRADecZoom({
    raRad: raDec.raRad,
    decRad: raDec.decRad,
    zoomDeg: zoomDeg ?? MAX_ZOOM,
    instant: true,
  });
}

function updateWWTLocation(location: LocationDeg) {
  wwtSettings.set_locationLat(location.latitudeDeg);
  wwtSettings.set_locationLng(location.longitudeDeg);
}

function doWWTModifications() {
  Grids.drawEcliptic = drawEcliptic;

  // We need to render one frame ahead of time
  // as there's a lot of setup done on the first frame
  // render that we need to use
  WWTControl.singleton.renderOneFrame();

  const boundRenderOneFrame = renderOneFrame.bind(WWTControl.singleton);
  const newFrameRender = function() { 
    boundRenderOneFrame(
      showHorizon.value,
      showHorizon.value,
      false,
    );
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  WWTControl.singleton._drawSkyOverlays = drawSkyOverlays.bind(WWTControl.singleton);

  // as well as our custom text overlays
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  WWTControl.singleton.renderOneFrame = newFrameRender;

  const originalUpdatePlanetLocations = Planets.updatePlanetLocations;
  function newUpdatePlanetLocations(threeD: boolean) {
    originalUpdatePlanetLocations(threeD);
    // Only scale the Sun (index 0)
    // Use smaller scale (1) during polar night, normal scale (4) otherwise
    const polarInfo = currentDayInfo.value[2];
    Planets._planetScales[0] = Planets._planetScales[0] * (polarInfo.sunAlwaysDown ? 1 : 4);
  }
  Planets.updatePlanetLocations = newUpdatePlanetLocations;
  Planets.drawPlanets = drawPlanets;
}

watch(selectedLocation, (location: LocationDeg, oldLocation: LocationDeg) => {
  updateSelectedLocationInfo();
  updateWWTLocation(location);
  updateSliderBounds(location, oldLocation);
  resetView();
});

watch(currentTime, (time: Date) => {
  // Auto-pause when time reaches sunset or sunrise, accounting for playing direction
  if (playing.value && ((time.getTime() >= endTime.value && store.clockRate >= 0) || ( time.getTime() <= startTime.value && store.clockRate <= 0))) {
    playing.value = false;
    return;
  }  
  if (forceCamera.value) {
    resetView(store.zoomDeg);
  }
  sunDistance.value = sunPlace.get_distance();
});

watch(playing, (play: boolean) => {
  store.setClockRate(play ? 1000 : 0);
  store.setClockSync(play);
});

watch(forceCamera, (value: boolean) => {
  if (value) {
    resetView();
  }
});

watch(selectedEvent, (event: EventOfInterest | null) => {
  if (!event) {
    return;
  }

  goToEvent(event);
  const representation = event === "custom" ? `event_custom ${selectedCustomDate.value?.toISOString()}` : event as string;
  userSelectedDates.push(representation);
  events.push(representation);
});

watch(selectedCustomDate, (date: Date | null) => {
  if (date && selectedEvent.value === "custom") {
    goToEvent("custom");

    const representation = `event_custom ${selectedCustomDate.value?.toISOString()}`;
    userSelectedDates.push(representation);
    events.push(representation);
  }
});

watch(inNorthernHemisphere, (_inNorth: boolean) => resetNSEWText());
watch(splashReady, (ready: boolean) => {
  if (ready) {
    nextTick(() => {
      if (splashScreenRef.value) {
        const firstFocusable = splashScreenRef.value.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }
    });
  }
});

function updateTabTime(leavingTab: number, timestamp: number) {
  if (leavingTab === 0) {
    howToUseTimeMs += (timestamp - (howToUseStartTimestamp ?? timestamp));
    howToUseStartTimestamp = null;
  } else {
    whatToExploreTimeMs += (timestamp - (whatToExploreStartTimestamp ?? timestamp));
    whatToExploreStartTimestamp = null;
  }
}

function startTabTime(enteringTab: number, timestamp: number) {
  if (enteringTab === 0) {
    howToUseStartTimestamp = timestamp;
  } else {
    whatToExploreStartTimestamp = timestamp;    
  }
}

watch(showTextSheet, (show: boolean) => {
  const now = Date.now();
  if (show) {
    startTabTime(tab.value, now);
  } else {
    updateTabTime(tab.value, now);
  }
});

watch(tab, (newTab: number) => {
  // This shouldn't ever happen
  if (!showTextSheet.value) {
    return;
  }
  // We're going to `newTab`
  // The tab indices are 0 and 1, so we're leaving `1 - newTab`
  const now = Date.now();
  startTabTime(newTab, now);
  updateTabTime(1 - newTab, now);
});


const STORY_DATA_URL = `${API_BASE_URL}/seasons/data`;
const storedOptOut = window.localStorage.getItem(OPT_OUT_KEY);
const storedAhaOptOut = window.localStorage.getItem(AHA_OPT_OUT_KEY);
const maybeUUID = window.localStorage.getItem(UUID_KEY);
const optOut = typeof storedOptOut === "string" ? storedOptOut === "true" : null;
const responseOptOut = ref(optOut);
const ahaOptOut = ref(typeof storedAhaOptOut === "string" ? storedAhaOptOut === "true" : null);
const showPrivacyDialog = ref(false);
const existingUser = maybeUUID !== null;
const uuid = maybeUUID ?? v4();
if (!existingUser) {
  window.localStorage.setItem(UUID_KEY, uuid);
}

let timeSliderUsedCount = 0;
let events: string[] = [];
let userSelectedDates: string[] = [];
let userSelectedLocations: [number, number][] = [];
let ahaMomentResponses: string[] = [];
let appStartTimestamp = Date.now();

function onTimeSliderEnd(_value: number) {
  timeSliderUsedCount += 1;
  events.push("time_slider_used");
}

async function questionDisplaySetup() {
  if (responseOptOut.value || ahaOptOut.value) {
    return;
  }

  const existingDataResponse = await fetch(`${STORY_DATA_URL}/${uuid}`, {
    method: "GET",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
  });

  const existingDataContent = await existingDataResponse.json();
  const alreadyAnswered = existingDataResponse.status === 200 && existingDataContent.response.aha_moment_responses.length > 0;

  if (alreadyAnswered) {
    return;
  }

  setQuestionTimeout();
}

function setQuestionTimeout(timeout=3 * 60_000) {
  questionTimeout = setTimeout(() => {
    showQuestion.value = true;
  }, timeout);
}

async function createUserEntry() {
  if (responseOptOut.value) {
    return;
  }

  const response = await fetch(`${STORY_DATA_URL}/${uuid}`, {
    method: "GET",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" },
  });
  const content = await response.json();
  const exists = response.status === 200 && content.response?.user_uuid != undefined;
  if (exists) {
    return;
  }

  fetch(`${STORY_DATA_URL}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
    },
    body: JSON.stringify({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_uuid: uuid,
    }),
  });
}

function resetData() {
  howToUseTimeMs = 0;
  whatToExploreTimeMs = 0;

  timeSliderUsedCount = 0;
  events = [];
  userSelectedDates = [];
  userSelectedLocations = [];
  ahaMomentResponses = [];
  Object.assign(wwtStats, {
    timeResetCount: 0,
    reverseCount: 0,
    playPauseCount: 0,
    speedups: [],
    slowdowns: [],
    rateSelections: [],
    startTime: selectedTime.value,
  });

  const now = Date.now();
  appStartTimestamp = now;
  howToUseStartTimestamp = (showTextSheet.value && tab.value == 0) ? now : null;
  whatToExploreStartTimestamp = (showTextSheet.value && tab.value == 1) ? now : null;
}

function updateUserData() {
  if (responseOptOut.value) {
    return;
  }

  const now = Date.now();
  const howToUseTime = (showTextSheet.value && howToUseStartTimestamp !== null) ? now - howToUseStartTimestamp : howToUseTimeMs;
  const whatToExploreTime = (showTextSheet.value && whatToExploreStartTimestamp !== null) ? now - whatToExploreStartTimestamp : whatToExploreTimeMs;
  const body = {
    events,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    time_slider_used_count: timeSliderUsedCount,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    user_selected_dates: userSelectedDates,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    user_selected_locations: userSelectedLocations,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    app_time_ms: now - appStartTimestamp,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    how_to_use_time_ms: howToUseTime,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    what_to_explore_time_ms: whatToExploreTime,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wwt_time_reset_count: wwtStats.timeResetCount,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wwt_reverse_count: wwtStats.reverseCount,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wwt_play_pause_count: wwtStats.playPauseCount,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wwt_speedups: wwtStats.speedups,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wwt_slowdowns: wwtStats.slowdowns,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wwt_rate_selections: wwtStats.rateSelections,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wwt_start_stop_times: [wwtStats.startTime, selectedTime.value],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    aha_moment_response: ahaMomentResponses,
  } as Record<string, unknown>;
  fetch(`${STORY_DATA_URL}/${uuid}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
    },
    body: JSON.stringify(body),
    keepalive: true,
  }).then(() => resetData());
}

watch(showSplashScreen, (show: boolean) => {
  if (!show && responseOptOut.value === null) {
    showPrivacyDialog.value = true; 
  }
});

watch(responseOptOut, (optOut: boolean | null) => {
  if (optOut !== null) {
    window.localStorage.setItem(OPT_OUT_KEY, String(optOut));
  }
});

watch(ahaOptOut, (optOut: boolean | null) => {
  if (optOut !== null) {
    window.localStorage.setItem(AHA_OPT_OUT_KEY, String(optOut));
  }
});

watch(showQuestion, (show: boolean) => {
  if (show && questionTimeout) {
    clearTimeout(questionTimeout);
  }
});

window.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    updateUserData();
  } else {
    resetData();
  }
});

window.onbeforeunload = updateUserData;



return (_ctx: any,_cache: any) => {
  const _component_WorldWideTelescope = _resolveComponent("WorldWideTelescope")!
  const _component_splash_screen = _resolveComponent("splash-screen")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_font_awesome_layers = _resolveComponent("font-awesome-layers")!
  const _component_geolocation_button = _resolveComponent("geolocation-button")!
  const _component_location_search = _resolveComponent("location-search")!
  const _component_location_selector = _resolveComponent("location-selector")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_icon_button = _resolveComponent("icon-button")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_date_picker = _resolveComponent("v-date-picker")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_slider = _resolveComponent("v-slider")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_speed_control = _resolveComponent("speed-control")!
  const _component_daylight_pie_chart = _resolveComponent("daylight-pie-chart")!
  const _component_credit_logos = _resolveComponent("credit-logos")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_funding_acknowledgement = _resolveComponent("funding-acknowledgement")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_question_dialog = _resolveComponent("question-dialog")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_app = _resolveComponent("v-app")!
  const _directive_ripple = _resolveDirective("ripple")!

  return (_openBlock(), _createBlock(_component_v_app, {
    id: "app",
    style: _normalizeStyle(cssVars.value)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_WorldWideTelescope),
        (splashReady.value)
          ? (_openBlock(), _createBlock(_component_splash_screen, {
              key: 0,
              ref_key: "splashScreenRef",
              ref: splashScreenRef,
              title: "Seasons",
              cssVars: cssVars.value,
              onClose: closeSplashScreen,
              onLocationSelected: setLocationFromSearchFeature
            }, null, 8, ["cssVars"]))
          : _createCommentVNode("", true),
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_2, [...(_cache[45] || (_cache[45] = [
              _createElementVNode("div", { class: "container" }, [
                _createElementVNode("div", { class: "spinner" }),
                _createElementVNode("p", null, "Loading …")
              ], -1)
            ]))], 512), [
              [_vShow, isLoading.value]
            ])
          ]),
          _: 1
        }),
        _createElementVNode("div", {
          id: "top-content",
          inert: showSplashScreen.value || undefined
        }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("button", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (showLocationSelector.value = !showLocationSelector.value)),
                  class: "icon-location-button clickable-object"
                }, [
                  _createVNode(_component_font_awesome_layers, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_font_awesome_icon, {
                        icon: "location-dot",
                        color: "black"
                      }),
                      _createVNode(_component_font_awesome_icon, {
                        icon: "location-dot",
                        color: accentColor.value,
                        transform: "shrink-3"
                      }, null, 8, ["color"])
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("h4", {
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (showLocationSelector.value = !showLocationSelector.value)),
                  class: "clickable-object"
                }, "View from")
              ]),
              _createElementVNode("button", {
                id: "location-button",
                class: "event-button info-button",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (showLocationSelector.value = true))
              }, [
                (selectedLocationInfo.value.name)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("strong", null, _toDisplayString(selectedLocationInfo.value.name), 1)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", null, "Lat: " + _toDisplayString(selectedLocationInfo.value.latitude), 1),
                _createElementVNode("div", null, "Long: " + _toDisplayString(selectedLocationInfo.value.longitude), 1)
              ])
            ]),
            _createVNode(_component_v_dialog, {
              modelValue: showLocationSelector.value,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showLocationSelector).value = $event)),
              "max-width": "fit-content",
              transition: "slide-y-transition"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card, null, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_8, [
                      _createVNode(_component_font_awesome_icon, {
                        style: {"cursor":"pointer","z-index":"1000"},
                        icon: "xmark",
                        size: "xl",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (showLocationSelector.value = false)),
                        onKeyup: _cache[4] || (_cache[4] = _withKeys(($event: any) => (showLocationSelector.value = false), ["enter"])),
                        tabindex: "0",
                        color: "black"
                      })
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                      _createVNode(_component_geolocation_button, {
                        id: "location",
                        size: "30px",
                        density: "default",
                        elevation: "5",
                        color: accentColor.value,
                        onGeolocation: _cache[5] || (_cache[5] = ($event: any) => (selectedLocation.value = {longitudeDeg: $event.longitude, latitudeDeg: $event.latitude}))
                      }, null, 8, ["color"]),
                      _createVNode(_component_location_search, {
                        class: _normalizeClass(['location-search']),
                        small: "",
                        "button-size": "xl",
                        "accent-color": accentColor.value,
                        "search-provider": searchProvider,
                        onSetLocation: setLocationFromSearchFeature,
                        onError: _cache[6] || (_cache[6] = ($event: any) => (searchErrorMessage.value = $event))
                      }, null, 8, ["accent-color"])
                    ]),
                    _createVNode(_component_location_selector, {
                      "model-value": selectedLocation.value,
                      "onUpdate:modelValue": updateLocationFromMap
                    }, null, 8, ["model-value"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"]),
            _createElementVNode("div", _hoisted_10, [
              _createVNode(_component_v_checkbox, {
                modelValue: trackSun.value,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((trackSun).value = $event)),
                label: "Auto-track Sun",
                density: "compact",
                "hide-details": "",
                onKeydown: [
                  _cache[9] || (_cache[9] = _withKeys(_withModifiers(($event: any) => (forceCamera.value = !forceCamera.value), ["prevent"]), ["space"])),
                  _cache[10] || (_cache[10] = _withKeys(_withModifiers(($event: any) => (forceCamera.value = !forceCamera.value), ["prevent"]), ["enter"]))
                ]
              }, null, 8, ["modelValue"])
            ]),
            false
              ? (_openBlock(), _createBlock(_component_icon_button, {
                  key: 0,
                  onActivate: goToSun,
                  icon: "fa-sun",
                  color: accentColor.value,
                  "tooltip-text": 'Go to Sun',
                  "tooltip-location": "start",
                  size: "sm"
                }, null, 8, ["color"]))
              : _createCommentVNode("", true),
            _createVNode(_component_icon_button, {
              modelValue: showTextSheet.value,
              "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((showTextSheet).value = $event)),
              icon: "fa-info",
              color: accentColor.value,
              "tooltip-text": showTextSheet.value ? 'Hide Info' : 'Learn More',
              "tooltip-location": "start",
              size: "sm"
            }, null, 8, ["modelValue", "color", "tooltip-text"])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("button", {
                onClick: _cache[12] || (_cache[12] = ($event: any) => (showDatePicker.value = !showDatePicker.value)),
                class: "display-date-button mr-2 clickable-object"
              }, [
                _createVNode(_component_font_awesome_layers, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, {
                      icon: "calendar-day",
                      color: "black"
                    }),
                    _createVNode(_component_font_awesome_icon, {
                      icon: "calendar-day",
                      color: accentColor.value,
                      transform: "shrink-3"
                    }, null, 8, ["color"])
                  ]),
                  _: 1
                })
              ]),
              _createElementVNode("h4", {
                onClick: _cache[13] || (_cache[13] = ($event: any) => (showDatePicker.value = !showDatePicker.value)),
                class: "clickable-object"
              }, "Displayed Date")
            ]),
            _createElementVNode("button", {
              id: "date-info",
              onClick: _cache[14] || (_cache[14] = ($event: any) => (showDatePicker.value = !showDatePicker.value)),
              class: "event-button info-button"
            }, [
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("strong", null, _toDisplayString(dayString(displayedDate.value)), 1)
              ]),
              _createElementVNode("div", null, "Length of Day: " + _toDisplayString(formatDayLength(endTime.value - startTime.value, currentDayInfo.value[2])), 1),
              _createElementVNode("div", null, "Distance to Sun: " + _toDisplayString(sunDistance.value.toFixed(2)) + " au", 1)
            ]),
            _createElementVNode("div", _hoisted_14, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedDatesOfInterest.value, ([event], index) => {
                return _withDirectives((_openBlock(), _createElementBlock("button", {
                  class: _normalizeClass([[event === selectedEvent.value ? 'selected' : ''], "event-button"]),
                  key: index,
                  onClick: ($event: any) => {selectedEvent.value = event;}
                }, [
                  _createElementVNode("div", null, _toDisplayString(eventName(event)), 1)
                ], 10, _hoisted_15)), [
                  [_directive_ripple]
                ])
              }), 128)),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("button", {
                  onClick: _cache[15] || (_cache[15] = ($event: any) => (showDatePicker.value = !showDatePicker.value)),
                  color: accentColor.value,
                  variant: "outlined",
                  size: "small",
                  class: "calendar-button event-button"
                }, [
                  _createVNode(_component_font_awesome_icon, { icon: "calendar-day" }),
                  _cache[46] || (_cache[46] = _createTextVNode(" Choose Any Date ", -1))
                ], 8, _hoisted_17),
                _createVNode(_component_v_dialog, {
                  modelValue: showDatePicker.value,
                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showDatePicker).value = $event)),
                  "max-width": "fit-content"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card_title, { class: "text-h6 mb-0 mt-2" }, {
                          default: _withCtx(() => [...(_cache[47] || (_cache[47] = [
                            _createTextVNode(" Select Date ", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(_component_v_card_text, { class: "my-0 mx-2 pa-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_date_picker, {
                              "model-value": selectedCustomDate.value,
                              "onUpdate:modelValue": handleDateSelection,
                              color: accentColor.value,
                              "hide-header": ""
                            }, null, 8, ["model-value", "color"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_card_actions, { class: "my-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_btn, {
                              text: "",
                              onClick: _cache[16] || (_cache[16] = ($event: any) => (showDatePicker.value = false))
                            }, {
                              default: _withCtx(() => [...(_cache[48] || (_cache[48] = [
                                _createTextVNode(" Cancel ", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ])
            ])
          ])
        ], 8, _hoisted_3),
        _createElementVNode("div", {
          id: "bottom-content",
          inert: showSplashScreen.value || undefined
        }, [
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("div", _hoisted_20, [
              (!smallSize.value)
                ? (_openBlock(), _createBlock(_component_v_slider, {
                    key: 0,
                    min: sliderMin,
                    max: sliderMax,
                    value: null,
                    disabled: "",
                    class: "dummy-time-slider"
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_slider, {
                modelValue: sliderValue.value,
                "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((sliderValue).value = $event)),
                color: sunAlways.value === 'down' ? NIGHTTIME : accentColor.value,
                min: sliderMin,
                max: sliderMax,
                "thumb-label": "always",
                class: _normalizeClass(['time-slider', { 'time-slider-large': !smallSize.value }]),
                onEnd: onTimeSliderEnd
              }, {
                "thumb-label": _withCtx(() => [
                  _createElementVNode("div", _hoisted_21, _toDisplayString(selectedLocaledTimeDateString.value), 1)
                ]),
                _: 1
              }, 8, ["modelValue", "color", "class"])
            ]),
            _createElementVNode("div", _hoisted_22, [
              _createVNode(_component_v_chip, {
                onClick: _cache[19] || (_cache[19] = () => {
              sliderValue.value = sliderMin;
              resetView(MAX_ZOOM);
              _unref(events).push('sunrise');
            }),
                color: accentColor.value,
                variant: "elevated",
                size: "x-small",
                class: "time-chip"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString((currentDayInfo.value[2].sunAlwaysDown || currentDayInfo.value[2].sunAlwaysUp) ?  'Midnight' : 'Sunrise'), 1)
                ]),
                _: 1
              }, 8, ["color"]),
              _createVNode(_component_v_chip, {
                onClick: _cache[20] || (_cache[20] = () => {
              sliderValue.value = (sliderMin + sliderMax) / 2;
              resetView(MAX_ZOOM);
              _unref(events).push('midday');
            }),
                color: accentColor.value,
                variant: "elevated",
                size: "x-small",
                class: "time-chip"
              }, {
                default: _withCtx(() => [...(_cache[49] || (_cache[49] = [
                  _createTextVNode(" Midday ", -1)
                ]))]),
                _: 1
              }, 8, ["color"]),
              _createVNode(_component_v_chip, {
                onClick: _cache[21] || (_cache[21] = () => {
              sliderValue.value = sliderMax;
              resetView(MAX_ZOOM);
              _unref(events).push('sunset');
            }),
                color: accentColor.value,
                variant: "elevated",
                size: "x-small",
                class: "time-chip"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString((currentDayInfo.value[2].sunAlwaysDown || currentDayInfo.value[2].sunAlwaysUp) ?  'Midnight' : 'Sunset'), 1)
                ]),
                _: 1
              }, 8, ["color"])
            ])
          ]),
          (!smallSize.value)
            ? (_openBlock(), _createBlock(_component_speed_control, {
                key: 0,
                "model-value": playing.value,
                store: _unref(store),
                color: accentColor.value,
                "default-rate": 1000,
                "max-speed": 10000,
                "rate-delta": 5,
                "show-status": "",
                "hide-more-controls": true,
                onReset: _cache[22] || (_cache[22] = () => {
          selectedEvent.value && goToEvent(selectedEvent.value);
          _unref(wwtStats).timeResetCount += 1;
          _unref(events).push('wwt_time_reset');
        }),
                "onUpdate:reverse": _cache[23] || (_cache[23] = (_reverse) => {
          _unref(wwtStats).reverseCount += 1;
          _unref(events).push('wwt_reverse');
        }),
                "onUpdate:modelValue": handlePlaying,
                onSlowDown: _cache[24] || (_cache[24] = (rate) => {
          _unref(wwtStats).slowdowns.push(rate);
          _unref(events).push(`wwt_slowdown ${rate}`);
        }),
                onSpeedUp: _cache[25] || (_cache[25] = (rate) => {
          _unref(wwtStats).speedups.push(rate);
          _unref(events).push(`wwt_speedup ${rate}`);
        }),
                onSetRate: _cache[26] || (_cache[26] = (rate) => {
          _unref(wwtStats).rateSelections.push(rate);
          _unref(events).push(`wwt_rate ${rate}`);
        })
              }, null, 8, ["model-value", "store", "color"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_23, [
                _createVNode(_component_icon_button, {
                  id: "play-pause",
                  icon: playing.value ? 'pause' : 'play',
                  onActivate: _cache[27] || (_cache[27] = () => {
            playing.value = !playing.value;
            handlePlaying(playing.value);
          }),
                  "tooltip-text": playing.value ? 'Pause' : 'Play',
                  "tooltip-offset": "5px",
                  "show-tooltip": "",
                  color: accentColor.value,
                  "focus-color": accentColor.value
                }, null, 8, ["icon", "tooltip-text", "color", "focus-color"]),
                _createVNode(_component_daylight_pie_chart, {
                  rise: startTime.value,
                  set: endTime.value,
                  always: sunAlways.value,
                  size: "50px",
                  "timezone-offset": _unref(selectedTimezoneOffset)
                }, null, 8, ["rise", "set", "always", "timezone-offset"])
              ]))
        ], 8, _hoisted_18),
        _createElementVNode("div", {
          id: "change-flags",
          inert: showSplashScreen.value || undefined
        }, [
          _createVNode(_component_icon_button, {
            icon: "mdi-comment-quote",
            onActivate: _cache[28] || (_cache[28] = ($event: any) => (showQuestion.value = true)),
            color: accentColor.value,
            "focus-color": accentColor.value,
            "tooltip-text": "Share your thoughts",
            "tooltip-location": "bottom",
            "tooltip-offset": "5px",
            "show-tooltip": !mobile.value,
            size: "1.2em"
          }, null, 8, ["color", "focus-color", "show-tooltip"]),
          _createVNode(_component_icon_button, {
            icon: "mdi-lock",
            onActivate: _cache[29] || (_cache[29] = () => showPrivacyDialog.value = true),
            color: accentColor.value,
            "focus-color": accentColor.value,
            "tooltip-text": "Change privacy settings",
            "tooltip-location": "bottom",
            "tooltip-offset": "5px",
            "show-tooltip": !mobile.value,
            size: "1.2em"
          }, null, 8, ["color", "focus-color", "show-tooltip"])
        ], 8, _hoisted_24),
        (!smallSize.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 1,
              id: "body-logos",
              inert: showSplashScreen.value || undefined
            }, [
              _createVNode(_component_credit_logos, { "default-logos": ['cosmicds', 'wwt', 'sciact', 'nasa'] })
            ], 8, _hoisted_25))
          : _createCommentVNode("", true),
        _createVNode(_component_v_dialog, {
          class: "error-dialog",
          style: _normalizeStyle(cssVars.value),
          modelValue: showWebGL2Dialog.value,
          "onUpdate:modelValue": _cache[30] || (_cache[30] = ($event: any) => ((showWebGL2Dialog).value = $event)),
          persistent: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [...(_cache[50] || (_cache[50] = [
                _createElementVNode("div", { class: "error-message" }, [
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, "This app requires WebGL 2")
                  ]),
                  _createElementVNode("p", { class: "mt-2" }, " Check your browser's settings and enable WebGL 2 (\"graphics acceleration\" on some browsers). "),
                  _createElementVNode("p", { class: "mt-2" }, [
                    _createTextVNode(" You can check whether your browser supports WebGL 2 and get assistance "),
                    _createElementVNode("a", {
                      href: "https://get.webgl.org/webgl2/",
                      target: "_blank",
                      rel: "noopener noreferrer"
                    }, "here"),
                    _createTextVNode(". ")
                  ])
                ], -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["style", "modelValue"]),
        _createVNode(_component_v_dialog, {
          id: "video-container",
          modelValue: showVideoSheet.value,
          "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event: any) => ((showVideoSheet).value = $event)),
          transition: "slide-y-transition",
          fullscreen: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_26, [
              _createVNode(_component_font_awesome_icon, {
                id: "video-close-icon",
                class: "close-icon",
                icon: "times",
                size: "lg",
                onClick: _cache[31] || (_cache[31] = ($event: any) => (showVideoSheet.value = false)),
                onKeyup: _cache[32] || (_cache[32] = _withKeys(($event: any) => (showVideoSheet.value = false), ["enter"])),
                tabindex: "0"
              }),
              _cache[51] || (_cache[51] = _createElementVNode("video", {
                controls: "",
                id: "info-video"
              }, [
                _createElementVNode("source", {
                  src: "",
                  type: "video/mp4"
                })
              ], -1))
            ])
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_dialog, {
          style: _normalizeStyle(cssVars.value),
          class: _normalizeClass(['info-sheet', `info-sheet-${infoSheetLocation.value}`]),
          id: "text-info-sheet",
          "hide-overlay": "",
          persistent: "",
          "no-click-animation": "",
          absolute: "",
          width: "100%",
          scrim: false,
          location: "bottom",
          modelValue: showTextSheet.value,
          "onUpdate:modelValue": _cache[38] || (_cache[38] = ($event: any) => ((showTextSheet).value = $event)),
          transition: infoSheetTransition.value
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, { height: "100%" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_tabs, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[34] || (_cache[34] = ($event: any) => ((tab).value = $event)),
                  height: "32px",
                  color: accentColor.value,
                  "slider-color": accentColor.value,
                  id: "tabs",
                  dense: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_tab, {
                      class: "info-tabs",
                      tabindex: "0"
                    }, {
                      default: _withCtx(() => [...(_cache[52] || (_cache[52] = [
                        _createElementVNode("h3", null, "How to Use", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_tab, {
                      class: "info-tabs",
                      tabindex: "0"
                    }, {
                      default: _withCtx(() => [...(_cache[53] || (_cache[53] = [
                        _createElementVNode("h3", null, "What to Explore", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "color", "slider-color"]),
                _createVNode(_component_font_awesome_icon, {
                  id: "close-text-icon",
                  class: "control-icon",
                  icon: "times",
                  size: "lg",
                  onClick: _cache[35] || (_cache[35] = ($event: any) => (showTextSheet.value = false)),
                  onKeyup: _cache[36] || (_cache[36] = _withKeys(($event: any) => (showTextSheet.value = false), ["enter"])),
                  tabindex: "0"
                }),
                _createVNode(_component_v_window, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[37] || (_cache[37] = ($event: any) => ((tab).value = $event)),
                  id: "tab-items",
                  class: "pb-2 no-bottom-border-radius"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_window_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { class: "no-bottom-border-radius scrollable" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "info-text no-bottom-border-radius" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_container, null, {
                                  default: _withCtx(() => [
                                    _cache[82] || (_cache[82] = _createElementVNode("p", null, " This interactive lets you view the path of the Sun in the sky on the solstices and equinoxes from any location on Earth! ", -1)),
                                    _createElementVNode("ul", _hoisted_27, [
                                      _createElementVNode("li", _hoisted_28, [
                                        _cache[54] || (_cache[54] = _createTextVNode(" Click ", -1)),
                                        _createVNode(_component_font_awesome_icon, {
                                          icon: "location-dot",
                                          color: accentColor.value
                                        }, null, 8, ["color"]),
                                        _cache[55] || (_cache[55] = _createTextVNode(" in the upper left to choose a different location. ", -1))
                                      ]),
                                      _cache[73] || (_cache[73] = _createElementVNode("li", { class: "pb-3" }, " Click the buttons in the upper right to choose a different date. ", -1)),
                                      _cache[74] || (_cache[74] = _createElementVNode("li", null, " Use the time slider at the bottom to see how the Sun moves through the sky on your chosen date from your chosen location. ", -1)),
                                      _cache[75] || (_cache[75] = _createElementVNode("div", { class: "py-1 pl-3" }, " or ", -1)),
                                      _createElementVNode("li", null, [
                                        _cache[56] || (_cache[56] = _createTextVNode(" Press ", -1)),
                                        _createVNode(_component_font_awesome_icon, {
                                          icon: "play",
                                          color: accentColor.value
                                        }, null, 8, ["color"]),
                                        _cache[57] || (_cache[57] = _createTextVNode("/ ", -1)),
                                        _createVNode(_component_font_awesome_icon, {
                                          icon: "pause",
                                          color: accentColor.value
                                        }, null, 8, ["color"]),
                                        _cache[58] || (_cache[58] = _createTextVNode(" to start/stop time. ", -1))
                                      ]),
                                      _createElementVNode("li", null, [
                                        _createVNode(_component_font_awesome_icon, {
                                          icon: "angles-up",
                                          color: accentColor.value
                                        }, null, 8, ["color"]),
                                        _cache[59] || (_cache[59] = _createTextVNode(" and ", -1)),
                                        _createVNode(_component_font_awesome_icon, {
                                          icon: "angles-down",
                                          color: accentColor.value
                                        }, null, 8, ["color"]),
                                        _cache[60] || (_cache[60] = _createTextVNode(" to speed up and slow down time. ", -1))
                                      ]),
                                      _createElementVNode("li", null, [
                                        _createVNode(_component_v_icon, { color: accentColor.value }, {
                                          default: _withCtx(() => [...(_cache[61] || (_cache[61] = [
                                            _createTextVNode("mdi-step-backward-2", -1)
                                          ]))]),
                                          _: 1
                                        }, 8, ["color"]),
                                        _cache[63] || (_cache[63] = _createTextVNode("/", -1)),
                                        _createVNode(_component_v_icon, { color: accentColor.value }, {
                                          default: _withCtx(() => [...(_cache[62] || (_cache[62] = [
                                            _createTextVNode("mdi-step-forward-2", -1)
                                          ]))]),
                                          _: 1
                                        }, 8, ["color"]),
                                        _cache[64] || (_cache[64] = _createTextVNode(" to play time in reverse/forwards. ", -1))
                                      ]),
                                      _createElementVNode("li", null, [
                                        _createVNode(_component_font_awesome_icon, {
                                          icon: "rotate",
                                          color: accentColor.value
                                        }, null, 8, ["color"]),
                                        _cache[65] || (_cache[65] = _createTextVNode(" to reset the time and view to sunrise for the day. ", -1))
                                      ]),
                                      _cache[76] || (_cache[76] = _createElementVNode("div", { class: "py-1 pl-3" }, " or ", -1)),
                                      _createElementVNode("li", null, [
                                        _cache[69] || (_cache[69] = _createTextVNode(" Jump directly to ", -1)),
                                        _createVNode(_component_v_chip, {
                                          color: accentColor.value,
                                          variant: "elevated",
                                          size: "small",
                                          class: "time-chip"
                                        }, {
                                          default: _withCtx(() => [...(_cache[66] || (_cache[66] = [
                                            _createTextVNode(" Sunrise ", -1)
                                          ]))]),
                                          _: 1
                                        }, 8, ["color"]),
                                        _cache[70] || (_cache[70] = _createTextVNode(", ", -1)),
                                        _createVNode(_component_v_chip, {
                                          color: accentColor.value,
                                          variant: "elevated",
                                          size: "small",
                                          class: "time-chip"
                                        }, {
                                          default: _withCtx(() => [...(_cache[67] || (_cache[67] = [
                                            _createTextVNode(" Midday ", -1)
                                          ]))]),
                                          _: 1
                                        }, 8, ["color"]),
                                        _cache[71] || (_cache[71] = _createTextVNode(", or ", -1)),
                                        _createVNode(_component_v_chip, {
                                          color: accentColor.value,
                                          variant: "elevated",
                                          size: "small",
                                          class: "time-chip"
                                        }, {
                                          default: _withCtx(() => [...(_cache[68] || (_cache[68] = [
                                            _createTextVNode(" Sunrise ", -1)
                                          ]))]),
                                          _: 1
                                        }, 8, ["color"]),
                                        _cache[72] || (_cache[72] = _createTextVNode(" using the buttons under the time slider. ", -1))
                                      ]),
                                      _cache[77] || (_cache[77] = _createElementVNode("li", { class: "py-3" }, " Move around the sky: ", -1))
                                    ]),
                                    _createVNode(_component_v_row, {
                                      align: "center",
                                      class: "pb-5"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[78] || (_cache[78] = [
                                                _createTextVNode(" Pan ", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "press + drag" : "click + drag"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? ":" : "W-A-S-D"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "keys"), 1),
                                            _cache[79] || (_cache[79] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, { align: "center" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[80] || (_cache[80] = [
                                                _createTextVNode(" Zoom ", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "pinch in and out" : "scroll in and out"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? ":" : "I-O"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "keys"), 1),
                                            _cache[81] || (_cache[81] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_spacer, { class: "end-spacer" })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_window_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { class: "no-bottom-border-radius scrollable" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "info-text no-bottom-border-radius" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_container, null, {
                                  default: _withCtx(() => [
                                    _cache[84] || (_cache[84] = _createElementVNode("h3", { class: "pb-1" }, " The Reason for Seasons ", -1)),
                                    _cache[85] || (_cache[85] = _createElementVNode("p", null, " Earth's axis has a 23.4 degree tilt, which causes the seasons we experience. ", -1)),
                                    _cache[86] || (_cache[86] = _createElementVNode("p", null, " The key factors are how high in the sky the Sun gets, and how long it stays in the sky on a particular day. The higher the Sun and the longer it is in the sky, the more energy we receive. ", -1)),
                                    _cache[87] || (_cache[87] = _createElementVNode("p", null, " You can explore how the Sun moves in the sky on different dates for a single location, or for multiple locations. ", -1)),
                                    _cache[88] || (_cache[88] = _createElementVNode("h4", { class: "pt-2" }, "Explore a single location", -1)),
                                    _cache[89] || (_cache[89] = _createElementVNode("p", null, " For a single location, here are some questions you might try to answer: ", -1)),
                                    _cache[90] || (_cache[90] = _createElementVNode("ul", { class: "ml-5 pb-1 explore-list" }, [
                                      _createElementVNode("li", null, " What is the angle of the Sun in the sky when it reaches its peak height on each of the four equinox and solstice dates? "),
                                      _createElementVNode("li", null, " On which of those dates is the Sun's peak height the highest? "),
                                      _createElementVNode("li", null, " On which of those dates is the Sun's peak height the lowest? "),
                                      _createElementVNode("li", null, " How many hours is the Sun in the sky on each of the four equinox and solstice dates? "),
                                      _createElementVNode("li", null, " On which of those dates is the Sun in the sky for the most amount of time? "),
                                      _createElementVNode("li", null, " On which of those dates is the Sun in the sky for the least amount of time? "),
                                      _createElementVNode("li", null, " Why do you think the equinoxes are called equinoxes? "),
                                      _createElementVNode("li", null, " What are the differences in the peak heights for those four dates, and how does it relate to the tilt of the Earth's axis? "),
                                      _createElementVNode("li", null, " What is notable about where the Sun rises and sets on the equinoxes? ")
                                    ], -1)),
                                    _cache[91] || (_cache[91] = _createElementVNode("h4", { class: "pt-2" }, "Explore multiple locations", -1)),
                                    _cache[92] || (_cache[92] = _createElementVNode("p", null, " Use the location selector to try answering some of these questions: ", -1)),
                                    _cache[93] || (_cache[93] = _createElementVNode("ul", { class: "ml-5 pb-1 explore-list" }, [
                                      _createElementVNode("li", null, " What seasons would be experienced by someone at your latitude, but roughly on the opposite side of the equator? (For example, Cambridge, MA has a latitude 42° NORTH of the equator. How would the seasons compare at a latitude that is 42° SOUTH of the equator?) "),
                                      _createElementVNode("li", null, " At what latitude ranges will the Sun never rise or never set on the solstices? "),
                                      _createElementVNode("li", null, " What latitude ranges will never experience the Sun being directly overhead (at a sky angle of 90°) on any date? "),
                                      _createElementVNode("li", null, " What latitude ranges will experience equal day and night every single day? ")
                                    ], -1)),
                                    _createVNode(_component_v_row, { class: "mt-5" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "12" }, {
                                          default: _withCtx(() => [...(_cache[83] || (_cache[83] = [
                                            _createElementVNode("div", { class: "credits" }, [
                                              _createElementVNode("h3", null, "Credits:"),
                                              _createElementVNode("h4", null, [
                                                _createElementVNode("a", {
                                                  href: "https://www.cosmicds.cfa.harvard.edu/",
                                                  target: "_blank",
                                                  rel: "noopener noreferrer"
                                                }, "CosmicDS"),
                                                _createTextVNode(" Vue Data Stories Team:")
                                              ]),
                                              _createTextVNode(" Pat Udomprasert"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Jon Carifio"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Azmé Tariq"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Harry Houghton"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" John Lewis"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Alyssa Goodman"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Mary Dussault"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Harry Houghton"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Evaluator: Sue Sunbury"),
                                              _createElementVNode("br"),
                                              _createElementVNode("h4", { class: "pt-4" }, "WorldWide Telescope Team:"),
                                              _createTextVNode(" Peter Williams"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" A. David Weigel"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Jon Carifio"),
                                              _createElementVNode("br")
                                            ], -1)
                                          ]))]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_funding_acknowledgement)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_spacer, { class: "end-spacer" })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["style", "class", "modelValue", "transition"]),
        _createVNode(_component_v_dialog, {
          scrim: "false",
          modelValue: showPrivacyDialog.value,
          "onUpdate:modelValue": _cache[41] || (_cache[41] = ($event: any) => ((showPrivacyDialog).value = $event)),
          "max-width": "400px",
          id: "privacy-popup-dialog"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [...(_cache[94] || (_cache[94] = [
                    _createTextVNode(" To evaluate usage of this app, ", -1),
                    _createElementVNode("strong", null, "anonymized", -1),
                    _createTextVNode(" data may be collected, including user feedback and locations searched or selected on map. Places selected via geolocation services on your device are NOT collected. ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_card_actions, { class: "pt-3" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_spacer),
                    _createVNode(_component_v_btn, {
                      color: "#BDBDBD",
                      href: "https://www.cfa.harvard.edu/privacy-statement",
                      target: "_blank",
                      rel: "noopener noreferrer"
                    }, {
                      default: _withCtx(() => [...(_cache[95] || (_cache[95] = [
                        _createTextVNode(" Privacy Policy ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_btn, {
                      color: "#ff6666",
                      onClick: _cache[39] || (_cache[39] = () => {
              responseOptOut.value = true;
              showPrivacyDialog.value = false;
            })
                    }, {
                      default: _withCtx(() => [...(_cache[96] || (_cache[96] = [
                        _createTextVNode(" Opt out ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_btn, {
                      color: "green",
                      onClick: _cache[40] || (_cache[40] = () => {
              responseOptOut.value = false;
              showPrivacyDialog.value = false;
            })
                    }, {
                      default: _withCtx(() => [...(_cache[97] || (_cache[97] = [
                        _createTextVNode(" Allow ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_expand_transition, null, {
          default: _withCtx(() => [
            _withDirectives(_createVNode(_component_question_dialog, {
              onDismiss: _cache[42] || (_cache[42] = () => showQuestion.value = false),
              onOptOut: _cache[43] || (_cache[43] = () => {
          showQuestion.value = false;
          ahaOptOut.value = true;
        }),
              onFinish: _cache[44] || (_cache[44] = (response) => {
          _unref(ahaMomentResponses).push(response);
          showQuestion.value = false;
        })
            }, null, 512), [
              [_vShow, showQuestion.value]
            ])
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})